import { assetUrl } from "@/utils/cdn"
import { loadUrlToBlob } from "@/utils/video"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useEffect, useState } from "react"

interface BeforeAfterProps {
  source: string
  initialPercent?: number
  labels?: boolean
  video?: boolean
  className?: string
}

export const BeforeAfterHold = (
  props: BeforeAfterProps
) => {
  const {
    source,
    initialPercent,
    labels,
    video,
    className,
  } = props

  const [position, setPosition] = useState(
    initialPercent ?? 50
  )

  const [pressed, setPressed] = useState(false)

  const { t } = useTranslation()
  const [loadedVideos, setLoadedVideos] =
    useState<LoadedVideos | null>(null)

  useEffect(() => {
    if (!video) {
      return
    }

    Promise.all([
      loadUrlToBlob(
        assetUrl(`/tools/${source}-before.mp4`)
      ),
      loadUrlToBlob(assetUrl(`/tools/${source}-after.mp4`)),
    ])
      .then(([before, after]) => {
        setLoadedVideos({
          before: URL.createObjectURL(before),
          after: URL.createObjectURL(after),
        })
      })
      .catch(console.error)
  }, [video, source])

  const labelClasses =
    "absolute bottom-4 px-[24px] py-[6px] pointer-events-none bg-color-black/40 rounded-full uppercase text-color-white font-600"

  const beforeClasses =
    "absolute h-full w-full top-0 left-0 pointer-events-none object-cover object-left inset-0"

  return (
    <div
      className={clsx(
        "relative cursor-ew-resize",
        className
      )}
      onPointerDown={() => setPressed(true)}
      onPointerLeave={() => setPressed(false)}
      onPointerUp={() => setPressed(false)}
      onPointerMove={(event) => {
        if (!pressed) {
          return
        }

        const { x, width } =
          event.currentTarget.getBoundingClientRect()

        const ratio = (event.clientX - x) / width

        setPosition(ratio * 100)
      }}>
      {video ? (
        loadedVideos ? (
          <>
            <video
              autoPlay
              playsInline
              loop
              muted
              style={{ width: position + "%" }}
              className={beforeClasses}
              src={loadedVideos.before}
            />
            <video
              autoPlay
              playsInline
              loop
              muted
              className="pointer-events-none h-full w-full object-cover object-left"
              src={loadedVideos.after}
            />
          </>
        ) : (
          <img
            src={assetUrl(
              `/tools/${source}-thumbnail.webp`
            )}
            alt="skeleton"
          />
        )
      ) : (
        <>
          <img
            src={assetUrl(`/tools/${source}-before.webp`)}
            alt="before"
            style={{ width: position + "%" }}
            className={beforeClasses}
          />
          <img
            src={assetUrl(`/tools/${source}-after.webp`)}
            alt="after"
            className="pointer-events-none h-full w-full object-cover object-left"
          />
        </>
      )}
      <div
        className="pointer-events-none absolute top-0  z-50 flex h-full w-[42px] flex-col items-center"
        style={{ left: `calc(${position}% - 21px)` }}>
        <div className="w-1 flex-1 bg-color-white" />
        <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full border-4 border-color-white backdrop-blur">
          <svg
            width="26"
            height="12"
            viewBox="0 0 26 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 1L24.0781 5.23178C24.5579 5.63157 24.5579 6.36843 24.0781 6.76822L19 11"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M7 1L1.92186 5.23178C1.44211 5.63157 1.44211 6.36843 1.92187 6.76822L7 11"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="w-1 flex-1 bg-color-white" />
      </div>

      {labels && (
        <>
          <span className={clsx("left-4", labelClasses)}>
            {t("lbl_before")}
          </span>
          <span className={clsx("right-4", labelClasses)}>
            {t("lbl_after")}
          </span>
        </>
      )}
    </div>
  )
}

interface LoadedVideos {
  before: string
  after: string
}

const BeforeAfter = (props: BeforeAfterProps) => {
  const {
    source,
    initialPercent,
    labels,
    video,
    className,
  } = props

  const [position, setPosition] = useState(
    initialPercent ?? 50
  )

  const { t } = useTranslation()
  const [loadedVideos, setLoadedVideos] =
    useState<LoadedVideos | null>(null)

  useEffect(() => {
    if (!video) {
      return
    }

    Promise.all([
      loadUrlToBlob(
        assetUrl(`/tools/${source}-before.mp4`)
      ),
      loadUrlToBlob(assetUrl(`/tools/${source}-after.mp4`)),
    ])
      .then(([before, after]) => {
        setLoadedVideos({
          before: URL.createObjectURL(before),
          after: URL.createObjectURL(after),
        })
      })
      .catch(console.error)
  }, [video, source])

  const labelClasses =
    "absolute bottom-[10px] text-[14px] desktop:px-[20px] desktop:py-[8px] pointer-events-none bg-color-black/40 px-3 py-1 rounded-full uppercase text-color-white font-600"

  const beforeClasses =
    "absolute h-full w-full top-0 left-0 pointer-events-none object-cover object-left inset-0"

  return (
    <div
      className={clsx(
        "relative cursor-ew-resize",
        className
      )}
      onPointerMove={(event) => {
        const { x, width } =
          event.currentTarget.getBoundingClientRect()

        const ratio = (event.clientX - x) / width

        setPosition(ratio * 100)
      }}>
      {video ? (
        loadedVideos ? (
          <>
            <video
              autoPlay
              playsInline
              loop
              muted
              style={{ width: position + "%" }}
              className={beforeClasses}
              src={loadedVideos.before}
            />
            <video
              autoPlay
              playsInline
              loop
              muted
              className="pointer-events-none h-full w-full object-cover object-left"
              src={loadedVideos.after}
            />
          </>
        ) : (
          <img
            src={assetUrl(
              `/tools/${source}-thumbnail.webp`
            )}
            alt="skeleton"
          />
        )
      ) : (
        <>
          <img
            src={assetUrl(`/tools/${source}-before.webp`)}
            alt="before"
            style={{ width: position + "%" }}
            className={beforeClasses}
          />
          <img
            src={assetUrl(`/tools/${source}-after.webp`)}
            alt="after"
            className="pointer-events-none h-full w-full object-cover object-left"
          />
        </>
      )}
      <div
        className="pointer-events-none absolute top-0  z-50 flex h-full w-[42px] flex-col items-center"
        style={{ left: `calc(${position}% - 21px)` }}>
        <div className="w-1 flex-1 bg-color-white" />
        <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full border-4 border-color-white backdrop-blur">
          <svg
            width="26"
            height="12"
            viewBox="0 0 26 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 1L24.0781 5.23178C24.5579 5.63157 24.5579 6.36843 24.0781 6.76822L19 11"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M7 1L1.92186 5.23178C1.44211 5.63157 1.44211 6.36843 1.92187 6.76822L7 11"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="w-1 flex-1 bg-color-white" />
      </div>

      {labels && (
        <>
          <span
            className={clsx("left-[10px]", labelClasses)}>
            {t("lbl_before")}
          </span>
          <span
            className={clsx("right-[10px]", labelClasses)}>
            {t("lbl_after")}
          </span>
        </>
      )}
    </div>
  )
}

interface BeforeAfterWithSmallCircleProps {
  before: string
  after: string
  initialPercent?: number
  video?: boolean
  labels?: boolean
  className?: string
}

export const BeforeAfterWithSmallCircle = (
  props: BeforeAfterWithSmallCircleProps
) => {
  const {
    before,
    after,
    initialPercent,
    video,
    className,
    labels,
  } = props

  const [position, setPosition] = useState(
    initialPercent ?? 50
  )

  const [loadedVideos, setLoadedVideos] =
    useState<LoadedVideos | null>(null)

  useEffect(() => {
    if (!video) {
      return
    }

    Promise.all([
      loadUrlToBlob(before),
      loadUrlToBlob(after),
    ])
      .then(([before, after]) => {
        setLoadedVideos({
          before: URL.createObjectURL(before),
          after: URL.createObjectURL(after),
        })
      })
      .catch(console.error)
  }, [video, before, after])

  const labelClasses =
    "absolute bottom-[10px] text-[14px] desktop:px-[20px] desktop:py-[8px] pointer-events-none bg-color-black/40 px-3 py-1 rounded-full uppercase text-color-white font-600"

  const beforeClasses =
    "absolute h-full w-full top-0 left-0 pointer-events-none object-cover object-left inset-0"

  return (
    <div
      className={clsx(
        "relative cursor-ew-resize",
        className
      )}
      onPointerMove={(event) => {
        const { x, width } =
          event.currentTarget.getBoundingClientRect()

        const ratio = (event.clientX - x) / width

        setPosition(ratio * 100)
      }}>
      {video ? (
        loadedVideos ? (
          <>
            <video
              autoPlay
              playsInline
              loop
              muted
              style={{ width: position + "%" }}
              className={beforeClasses}
              src={loadedVideos.before}
            />
            <video
              autoPlay
              playsInline
              loop
              muted
              className="pointer-events-none h-full w-full object-cover object-left"
              src={loadedVideos.after}
            />
          </>
        ) : (
          <video
            autoPlay
            playsInline
            loop
            muted
            src={after}
            className="pointer-events-none h-full w-full object-cover object-left"
          />
        )
      ) : (
        <>
          <img
            src={before}
            alt="before"
            style={{ width: position + "%" }}
            className={beforeClasses}
          />
          <img
            src={after}
            alt="after"
            className="pointer-events-none h-full w-full object-cover object-left"
          />
        </>
      )}
      <div
        className="pointer-events-none absolute top-0  z-50 flex h-full w-[42px] flex-col items-center"
        style={{ left: `calc(${position}% - 21px)` }}>
        <div className="w-1 flex-1 bg-color-white" />
        <div className="flex h-[21px] w-[21px] items-center justify-center rounded-full border-color-white bg-color-white">
          <svg
            width="13"
            height="7"
            viewBox="0 0 13 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.61252 1L1.33926 3.27326C1.14272 3.46979 1.14272 3.78845 1.33926 3.98499L3.61252 6.25824"
              stroke="#212127"
              strokeWidth="1.00654"
              strokeLinecap="round"
            />
            <path
              d="M9.65176 1L11.925 3.27326C12.1216 3.46979 12.1216 3.78845 11.925 3.98499L9.65176 6.25824"
              stroke="#212127"
              strokeWidth="1.00654"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="w-1 flex-1 bg-color-white" />
      </div>

      {labels && (
        <>
          <span
            className={clsx("left-[10px]", labelClasses)}>
            Before
          </span>
          <span
            className={clsx("right-[10px]", labelClasses)}>
            After
          </span>
        </>
      )}
    </div>
  )
}

export default BeforeAfter
